import React, { useState, useEffect } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { useVsiAppContext } from "../stores/VsiApp";
import styles from "../utils/themeStyles";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";
import { genericColors } from "../utils/chartUtils";

require("highcharts/indicators/indicators")(Highcharts);
require("highcharts/indicators/pivot-points")(Highcharts);
require("highcharts/indicators/macd")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/map")(Highcharts);

function BarChart({ categories, data, series, title = "", height, disableLegend, changeAxisTitle, scrollHeight, yAxisTitle, yAxisLabels, tooltip, config, yAxis }) {
	const { theme, isTabletScreen } = useVsiAppContext();
	const basicTextStyle = {
		color: styles[theme].textColor,
		fontFamily: styles[theme].fontFamily,
	};
	const updateSeries = (series, data) => {
		if (series) return series;
		else if (data)
			return [
				{
					data,
				},
			];
		return [];
	};

	const [chartOptions, setChartOptions] = useState({
			colors: config?.colors ? [...config.colors] : [...genericColors],
			credits: {
				enabled: false,
			},
			mapNavigation: {
				enabled: false
			},
			xAxis: {
				categories,
				labels: {
					style: basicTextStyle,
					step: 1,
				},
				scrollBar: {
				  enabled: true
				},
				scrollbar: {
					enabled: true,
					minWidth: 500,
				},
				min: 0,
				max: series?.[0]?.data?.length > 15 ? 15 : null,
			},
			yAxis: {
				title: {
					text: yAxisTitle ? yAxisTitle : changeAxisTitle ? "GiB" : "Size",
					style: {
					  ...basicTextStyle,
					  fontWeight: '600'
					}
				},
				labels: {
					style: basicTextStyle,
					allowOverlap: true,
					format: yAxisLabels || null,
				},
				...yAxis,
			},
			chart: {
				type: "bar",
				backgroundColor: "transparent",
				color: styles[theme].textColor,
				scrollbar: {
					enabled: true
				},
			},
			title: {
				text: title,
				style: basicTextStyle,
			},
			labels: {
				style: basicTextStyle,
			},
			legend: {
				itemStyle: basicTextStyle,
				align: "center",
				enabled: !disableLegend,
				y: 15,
				backgroundColor: styles['dark'].legendBackground
			},
			tooltip: {
				backgroundColor: styles[theme].altBgColor,
				style: basicTextStyle,
				...tooltip,
			},
			plotOptions: {
			  	series: {
					stacking: 'normal'
			  	}
			},
			series: updateSeries(series, data),
	});

	useEffect(() => {
		setChartOptions(() => {
			return {
				...chartOptions,
				xAxis: {
					...chartOptions.xAxis,
					categories,
					labels: {
						...chartOptions.xAxis.labels,
						style: basicTextStyle,
					},
					min: 0,
					tickLength: 0,
				},
				yAxis: {
					...chartOptions.yAxis,
					title: {
						...chartOptions.yAxis.title,
						text: yAxisTitle ? yAxisTitle : changeAxisTitle ? "GiB" : "Size",
                        style: {
                          ...basicTextStyle,
                          fontWeight: '600'
                        },
					},
					labels: {
						...chartOptions.yAxis.labels,
						style: basicTextStyle,
						format: yAxisLabels || null,
					},
					...yAxis,
				},
				chart: {
					...chartOptions.chart,
					color: styles[theme].textColor,
					scrollablePlotArea: {
					  minHeight: scrollHeight || 0,
					  scrollPositionX: 1
					},
					backgroundColor: "transparent",
					height: height || 500
				},
				title: {
					...chartOptions.title,
					style: basicTextStyle,
				},
				tooltip: {
					backgroundColor: styles[theme].altBgColor,
					style: basicTextStyle,
					...tooltip,
				},
				labels: {
					...chartOptions.labels,
					style: basicTextStyle,
				},
				legend: {
					...chartOptions.legend,
					itemStyle: basicTextStyle,
					align: "center",
					enabled: !disableLegend,
					backgroundColor: styles[theme].legendBackground
				},
				plotOptions: {
				  series: {
					stacking: 'normal'
				  }
				},
				series: updateSeries(series, data),
			};
		});
		// eslint-disable-next-line
	}, [categories, series, data, theme, isTabletScreen, disableLegend, yAxisTitle, yAxisLabels, tooltip, yAxis]);

	return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
}

BarChart.displayName = 'BarChart'

export default withErrorBoundary(BarChart, GeneralErrorBoundary);
